// Auth module — Firebase Auth REST API (no SDK, vanilla fetch)
// Handles: Google, Apple, Facebook OAuth + Email/password
// JWT stored in localStorage, verified by Cloudflare Worker

const FIREBASE_API_KEY  = 'AIzaSyAmB5A6aAnvSIPWQJNPB_lAGRiQqC-GUAg';
const FIREBASE_AUTH_URL = `https://identitytoolkit.googleapis.com/v1`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  uid:         string;
  email:       string | null;
  displayName: string | null;
  idToken:     string;
}

type AuthCallback = (user: AuthUser | null) => void;

// ─── State ────────────────────────────────────────────────────────────────────

let currentUser: AuthUser | null = restoreUser();
const listeners = new Set<AuthCallback>();

function restoreUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem('lava_auth_user');
    return raw ? JSON.parse(raw) as AuthUser : null;
  } catch { return null; }
}

function persistUser(user: AuthUser | null): void {
  if (user) localStorage.setItem('lava_auth_user', JSON.stringify(user));
  else       localStorage.removeItem('lava_auth_user');
  localStorage.setItem('lava_auth_token', user?.idToken ?? '');
}

function setUser(user: AuthUser | null): void {
  currentUser = user;
  persistUser(user);
  listeners.forEach(fn => fn(user));
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function getUser(): AuthUser | null {
  return currentUser;
}

export function onAuthChange(fn: AuthCallback): () => void {
  listeners.add(fn);
  fn(currentUser); // immediate call
  return () => listeners.delete(fn);
}

export function signOut(): void {
  setUser(null);
}

// ─── Email flow ───────────────────────────────────────────────────────────────

// Step 1: check if email exists → returns 'login' | 'register'
export async function checkEmail(email: string): Promise<'login' | 'register'> {
  const res = await firebasePost('accounts:createAuthUri', {
    identifier:    email,
    continueUri:   location.href,
  });
  return res.registered ? 'login' : 'register';
}

// Step 2a: existing user → sign in with password
export async function signInWithEmail(email: string, password: string): Promise<AuthUser> {
  const res = await firebasePost('accounts:signInWithPassword', {
    email,
    password,
    returnSecureToken: true,
  });
  const user = toAuthUser(res);
  setUser(user);
  return user;
}

// Step 2b: new user → create account
export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string
): Promise<AuthUser> {
  // Create account
  const res = await firebasePost('accounts:signUp', {
    email,
    password,
    returnSecureToken: true,
  });

  // Set display name
  await firebasePost('accounts:update', {
    idToken:     res.idToken,
    displayName,
    returnSecureToken: true,
  });

  const user = { ...toAuthUser(res), displayName };
  setUser(user);
  return user;
}

// Password reset email
export async function sendPasswordReset(email: string): Promise<void> {
  await firebasePost('accounts:sendOobCode', {
    requestType: 'PASSWORD_RESET',
    email,
  });
}

// ─── OAuth (Google / Apple / Facebook) ───────────────────────────────────────
// Uses Firebase's OAuth redirect flow.
// On redirect back, finishOAuthRedirect() completes sign-in.

export function startOAuthRedirect(provider: 'google' | 'apple' | 'facebook'): void {
  const providerId: Record<string, string> = {
    google:   'google.com',
    apple:    'apple.com',
    facebook: 'facebook.com',
  };

  const params = new URLSearchParams({
    providerId:  providerId[provider],
    continueUri: location.href,
    oauthScope:  'email profile',
  });

  // Get the auth redirect URL from Firebase
  firebasePost('accounts:createAuthUri', {
    providerId:  providerId[provider],
    continueUri: location.href,
  }).then(res => {
    sessionStorage.setItem('lava_oauth_pending', 'true');
    location.href = res.authUri;
  });
}

// Called on app boot — checks if returning from OAuth redirect
export async function finishOAuthRedirect(): Promise<AuthUser | null> {
  if (!sessionStorage.getItem('lava_oauth_pending')) return null;
  sessionStorage.removeItem('lava_oauth_pending');

  const res = await firebasePost('accounts:signInWithIdp', {
    requestUri:        location.href,
    returnSecureToken: true,
  });

  if (!res.idToken) return null;

  const user = toAuthUser(res);
  setUser(user);
  return user;
}

// ─── Token refresh ────────────────────────────────────────────────────────────

export async function refreshToken(): Promise<string | null> {
  const raw = localStorage.getItem('lava_auth_user');
  if (!raw) return null;

  const stored = JSON.parse(raw) as AuthUser & { refreshToken?: string };
  if (!stored.idToken) return null;

  // TODO: use refresh token to get a new idToken when expired
  // For MVP, return existing token (Firebase tokens last 1 hour)
  return stored.idToken;
}

// ─── Firebase REST helper ─────────────────────────────────────────────────────

async function firebasePost(endpoint: string, body: object): Promise<Record<string, string>> {
  const res = await fetch(`${FIREBASE_AUTH_URL}/${endpoint}?key=${FIREBASE_API_KEY}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });

  const data = await res.json() as Record<string, string> & { error?: { message: string } };

  if (!res.ok) {
    throw new AuthError(data.error?.message ?? 'Auth failed', res.status);
  }

  return data;
}

function toAuthUser(res: Record<string, string>): AuthUser {
  return {
    uid:         res.localId,
    email:       res.email ?? null,
    displayName: res.displayName ?? null,
    idToken:     res.idToken,
  };
}

// ─── Auth error ───────────────────────────────────────────────────────────────

export class AuthError extends Error {
  constructor(message: string, public status: number) {
    super(humanizeError(message));
    this.name = 'AuthError';
  }
}

function humanizeError(code: string): string {
  const map: Record<string, string> = {
    'EMAIL_NOT_FOUND':      'No account found with this email.',
    'INVALID_PASSWORD':     'Incorrect password.',
    'USER_DISABLED':        'This account has been disabled.',
    'EMAIL_EXISTS':         'An account with this email already exists.',
    'WEAK_PASSWORD':        'Password must be at least 6 characters.',
    'INVALID_EMAIL':        'Please enter a valid email address.',
    'TOO_MANY_ATTEMPTS_TRY_LATER': 'Too many attempts. Try again later.',
  };
  return map[code] ?? 'Something went wrong. Please try again.';
}
